<script setup lang="ts">
import { ManyAlumniActivitiesQuery } from '~/queries/many/alumni-activities'

interface AlumniActivityEntry {
  id: string
  title: string
  dateTimeStart?: string | null
  dateTimeEnd?: string | null
  visibleUntil?: string | null
  types?: Pick<AlumniActivityType, 'id' | 'title'>[] | null
  people?: Pick<Person, 'id' | 'title' | 'slug'>[] | null
  externalLink?: string | null
}

interface AlumniActivitiesData {
  docs: AlumniActivityEntry[]
  totalDocs: number
}

const { locale } = useI18n()
const now = cacheableNow()

const { data, error } = await useAsyncData<AlumniActivitiesData>(
  () => `home-page-alumni-activities-${locale.value}`,
  () => $fetch<AlumniActivitiesData>('/api/payload-gql', {
    method: 'POST',
    body: {
      query: ManyAlumniActivitiesQuery,
      variables: {
        now,
        locale: locale.value,
      },
    },
  })
)

const activities = computed(() => data.value?.docs ?? [])
</script>

<template>
  <div
    v-if="activities.length" 
    class="container-d px-1 mt-3 flex flex-col items-start"
  >
    <div class="mb-1 text-d uppercase">
      {{ $t('pages.home.alumniUpdates', { count: activities.length }) }}
    </div>

    <ul>
      <li
        v-for="item in activities"
        :key="item.id"
        class="list-sep border-current flex flex-col"
      >
        <div v-if="item.types?.length" class="text-d lowercase">
          {{ item.types.map((type) => type.title).join(', ') }}
        </div>

        <component
          :is="item.externalLink ? 'a' : 'div'"
          v-bind="item.externalLink ? { href: item.externalLink, target: '_blank', rel: 'noopener noreferrer' } : {}"
          class="inline-block"
        >
          <div 
            class="text-d flex flex-wrap gap-x-0_25"
            :class="{
              'hover:text-accent': item.externalLink
            }"
          >
            {{ item.title }}
          </div>

          <div
            v-if="item.dateTimeStart && item.dateTimeEnd"
            class="text-d"
          >
            {{ getFormattedDateRange(item.dateTimeStart, item.dateTimeEnd) }}
          </div>
        </component>

        <ul
          v-if="item.people?.length"
          class="text-d"
        >
          {{ $t('labels.artist', { count: item.people.length }) }}:
          <li
            v-for="(person, index) in item.people" :key="person.id"
            class="inline-block"
          >
            <shared-person-link :name="person.title" :slug="person.slug" /><span v-if="index < item.people.length - 1">,&nbsp;</span>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
